/**
 * 短时媒体访问令牌缓存（前端）
 *
 * 后端 GET /api/storage/media-token 签发 aud=media 的短时令牌（默认 30 分钟）。
 * withAuthToken 是同步函数（10+ 个组件直接调用），这里用「惰性刷新 + 内存缓存」
 * 保持同步签名：缓存新鲜直接返回；过期时触发后台刷新并返回旧值/回退值，
 * 下一次调用即可拿到新令牌。未登录或首次刷新未完成时返回 null，
 * 调用方回退完整账号 JWT（兼容旧行为）。
 */

import { platformFetch } from '../platform/http';
import { getActiveToken } from './authSession';

const REFRESH_MARGIN_MS = 60 * 1000; // 提前 1 分钟视为过期，触发刷新

let cached: { token: string; expiresAt: number } | null = null;
let refreshing: Promise<void> | null = null;

function isFresh(): boolean {
  return Boolean(cached && cached.expiresAt - Date.now() > REFRESH_MARGIN_MS);
}

async function refresh(): Promise<void> {
  const authToken = getActiveToken();
  if (!authToken) {
    cached = null;
    return;
  }
  try {
    // 统一走平台请求层：401 时清会话并广播，鉴权头由 platformFetch 处理
    const response = await platformFetch('/storage/media-token');
    if (!response.ok) {
      cached = null;
      return;
    }
    const body = await response.json();
    const token: string | undefined = body?.data?.token;
    const expiresIn: number | undefined = body?.data?.expiresIn;
    if (token && typeof expiresIn === 'number') {
      cached = {
        token,
        expiresAt: Date.now() + Math.max(0, expiresIn - 30) * 1000,
      };
    }
  } catch {
    // 网络失败保留旧缓存（未过期仍可用）；彻底失败时由调用方回退账号 JWT
  }
}

/**
 * 获取可用的媒体令牌（同步）。
 * - 缓存新鲜 → 返回媒体令牌
 * - 缓存过期/缺失且已登录 → 触发后台刷新；返回未过期的旧值，否则 null
 * - 未登录 → null
 */
export function getMediaToken(): string | null {
  if (isFresh()) return cached!.token;
  if (!getActiveToken()) {
    cached = null;
    return null;
  }
  if (!refreshing) {
    refreshing = refresh()
      .catch(() => undefined)
      .finally(() => {
        refreshing = null;
      });
  }
  if (cached && cached.expiresAt > Date.now()) return cached.token;
  return null;
}

/**
 * 确保媒体令牌就绪（异步）。
 * 应用挂载前 / 登录成功后调用，堵住「首帧渲染时缓存未就绪」的窗口——
 * 这是历史上用账号 JWT 兜底的原因，如今该回退已删除，冷启动必须预取。
 */
export function ensureMediaToken(): Promise<void> {
  if (isFresh() || !getActiveToken()) return Promise.resolve();
  if (!refreshing) {
    refreshing = refresh()
      .catch(() => undefined)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}
